import { useCallback, useEffect } from 'react';
import socketIOClient from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';

import { setConnection, setConnected, socketSelectors } from 'app/store/socketSlice';
import { authSelectors } from 'app/auth/store/userSlice';

import * as hooks from './hooks';
import { SOCKET_URL } from './constants';

export const useSocketIo = () => {
  const dispatch = useDispatch();
  const options = hooks.useOptions();
  const connection = useSelector(socketSelectors.selectConnection);
  const role = useSelector(authSelectors.currentRole);

  const onConnect = useCallback(() => {
    dispatch(setConnected(true));
  }, [dispatch]);

  const onDisconnect = useCallback(() => {
    dispatch(setConnected(false));
  }, [dispatch]);

  const onReconnectAttempt = useCallback(() => {
    if (typeof options.auth === 'function' && connection) {
      options.auth((authPayload) => {
        connection.auth = authPayload;
      });
    }
  }, [connection, options]);

  useEffect(() => {
    if (!role) return;

    const newConnection = socketIOClient(SOCKET_URL, options);
    dispatch(setConnection(newConnection));

    return () => {
      newConnection.disconnect();
      dispatch(setConnection(null));
    };
  }, [dispatch, options, role]);

  useEffect(() => {
    if (!connection) return;

    const onConnectError = (err) => {
      const isAuthError =
        err.message === 'Authentication error' ||
        err.data?.type === 'AuthError' ||
        err.data?.statusCode === 401;

      if (isAuthError) {
        setTimeout(() => {
          options.auth((payload) => {
            connection.auth = payload;
          });
          connection.connect();
        }, 2000);
      }
    };

    connection.on('connect', onConnect);
    connection.on('disconnect', onDisconnect);
    connection.on('connect_error', onConnectError);
    connection.io.on('reconnect_attempt', onReconnectAttempt);

    return () => {
      connection.off('connect', onConnect);
      connection.off('disconnect', onDisconnect);
      connection.off('connect_error', onConnectError);
      connection.io.off('reconnect_attempt', onReconnectAttempt);
    };
  }, [onConnect, connection, onDisconnect, onReconnectAttempt, options]);
};
